import { ActivatedRoute, NavigationEnd, Router } from '@angular/router'
import { Component, OnDestroy, OnInit } from '@angular/core'
import { Subject, filter, takeUntil } from 'rxjs'
import { MessageService } from 'primeng/api'

import { COMMITTEE_VIEWS, CommitteeView, LeaseGroup, VIEW_GROUP, ViewDef } from '../data/constants/committee.constants'
import { SessionCreated } from '../components/new-session-dialog/new-session-dialog.component'
import { ApiCommitteeService } from '@data/services/modules/committee/api-committee.service'
import { CommitteeShellService } from '../data/services/view/committee-shell.service'
import { CommitteeSummary } from '../data/interfaces/committee-session.interface'
import { AuthService } from '@data/services/modules/auth/auth.service'

/** Qué casilla de la franja detalla cada vista. */
type SummaryTile = 'sessions' | 'votes' | 'bring' | 'minutes'

/**
 * El hub del Comité de Aprobaciones: las sesiones que esperan voto, los arrendamientos que hay que
 * llevar y las actas que faltan, en una sola pantalla.
 *
 * **Es un CASCARÓN, y eso es deliberado** — el mismo molde que Cobranza (`cobranza-page`). Las vistas
 * cuelgan de `<router-outlet>` en vez de estar puestas con `*ngIf`: cambiar de pestaña no recrea el
 * encabezado ni las cuatro cifras, y cada vista lleva su propio par `module`/`permission` en su ruta.
 *
 * **Toast propio.** El header del panel ya monta un `<p-toast>` sobre el `MessageService` raíz; si esta
 * pantalla usara ese mismo servicio cada aviso saldría dos veces. Por eso declara su propio
 * `MessageService` y su propio `<p-toast>`; las vistas y el diálogo NO declaran provider —heredan este
 * por el inyector del outlet— y así avisan por el mismo canal.
 *
 * **El modal se enciende por la DIRECCIÓN** (`?llevar=id,id` o `?llevar=nuevo`), como el asistente de
 * carga de Cobranza: «atrás» lo cierra, y el enlace «Llevar a comité» del expediente del
 * arrendamiento aterriza aquí con su id ya puesto.
 */
@Component({
  selector: 'app-committees-page',
  templateUrl: './committees-page.component.html',
  styleUrls: ['./committees-page.component.scss'],
  providers: [MessageService],
})
export class CommitteesPageComponent implements OnInit, OnDestroy {
  readonly views: ViewDef[] = COMMITTEE_VIEWS

  view: CommitteeView = 'sesiones'
  summary: CommitteeSummary | null = null
  loadingSummary = false

  /** El modal de nueva sesión, y los arrendamientos con los que abre. */
  showNewSession = false
  preselectedIds: string[] = []

  /**
   * Si este usuario vota. Gatea el chip de pendientes: Legal —que crea y cierra pero NO vota (D-7)—
   * recibiría un «3 votos pendientes» que no son suyos.
   */
  readonly canVote: boolean

  private readonly destroy$ = new Subject<void>()

  constructor(
    public readonly authService: AuthService,
    private readonly shell: CommitteeShellService,
    private readonly api: ApiCommitteeService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly messageService: MessageService,
  ) {
    this.canVote = this.authService.hasModulePermission('committee', 'vote')
  }

  ngOnInit(): void {
    this.readChildRoute()
    //* La vista sale de la ruta HIJA, y al navegar entre hermanas este componente ya NO se recrea:
    //* hay que volver a leerla en cada navegación o el segmentado se queda en la anterior.
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.readChildRoute())

    this.route.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe((q) => {
      const llevar = q.get('llevar')
      this.showNewSession = !!llevar
      this.preselectedIds = llevar && llevar !== 'nuevo' ? llevar.split(',').filter(Boolean) : []
    })

    //* Una vista escribió algo que cambia las cifras —se votó, se cerró, se subió un acta—: se recarga.
    this.shell.refresh$.pipe(takeUntil(this.destroy$)).subscribe(() => this.loadSummary())

    this.loadSummary()
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  /** Las vistas que este rol puede abrir. La que no puede, no se pinta ni se monta. */
  get visibleViews(): ViewDef[] {
    return this.views.filter((v) => this.authService.hasModulePermission(v.perm[0], v.perm[1]))
  }

  /** En el detalle de una sesión las cuatro cifras se esconden: ahí manda la sesión. */
  get isDetail(): boolean {
    return this.view === 'sesion'
  }

  /**
   * Desde qué pestaña se abre el modal: decide el tipo de sesión por defecto. Desde Sesiones o desde
   * el detalle se asume «por originar», que es la razón de ser de una votación.
   */
  get origin(): LeaseGroup {
    return VIEW_GROUP[this.view] ?? 'originating'
  }

  /**
   * Cuántos votos le faltan AL USUARIO. Sale del mismo resumen que las cuatro cifras —no hay una
   * llamada aparte— y solo se enseña a quien tiene `committee.vote`.
   */
  get pendingVotesChip(): number {
    return this.canVote ? (this.summary?.myPendingVotes ?? 0) : 0
  }

  goTo(view: ViewDef): void {
    if (view.key === this.view) return

    //* Se NAVEGA. Cambiar de vista tiene que quedar en la barra de direcciones y en el historial.
    this.router.navigate([view.path])
  }

  openPending(): void {
    this.router.navigate(['/comites/sesiones'])
  }

  /** El sombreado marca la casilla que la vista abierta detalla. */
  isActiveTile(tile: SummaryTile): boolean {
    if (this.view === 'sesiones') return tile === 'sessions'
    if (this.view === 'cerrados') return tile === 'minutes'

    return tile === 'bring'
  }

  // ── El modal de nueva sesión ──

  openNewSession(): void {
    this.router.navigate([], { relativeTo: this.route, queryParams: { llevar: 'nuevo' }, queryParamsHandling: 'merge' })
  }

  closeNewSession(): void {
    this.router.navigate([], { relativeTo: this.route, queryParams: { llevar: null }, queryParamsHandling: 'merge' })
  }

  /** Cerrar por fuera (X, Escape) también tiene que limpiar la dirección, o al recargar reabre. */
  onDialogVisibleChange(visible: boolean): void {
    if (!visible && this.route.snapshot.queryParamMap.get('llevar')) this.closeNewSession()
  }

  /**
   * La sesión ya existe: se avisa, se recargan las cifras y se aterriza en ella. Si el correo no le
   * llegó a alguien, el MISMO aviso lo dice —un «Sesión creada» a secas escondería que hay un votante
   * que nunca se va a enterar—; desde la sesión se reenvía.
   */
  onSessionCreated(created: SessionCreated): void {
    const n = created.unnotified
    if (n > 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Sesión creada',
        detail: `No se pudo avisar por correo a ${n} ${n === 1 ? 'votante' : 'votantes'}. Reenvía el aviso desde la sesión.`,
        life: 8000,
      })
    } else {
      this.messageService.add({ severity: 'success', summary: 'Sesión creada', detail: 'La sesión del comité quedó registrada.', life: 4000 })
    }
    this.shell.refresh()
    //* Sin `queryParamsHandling`: al cambiar de ruta se suelta `?llevar=` y el modal se cierra solo.
    this.router.navigate(['/comites/sesion', created.id])
  }

  /** Qué vista está activa, leída de la ruta hija. */
  private readChildRoute(): void {
    let r = this.route.firstChild
    while (r?.firstChild) r = r.firstChild
    this.view = (r?.snapshot.data?.['view'] as CommitteeView) ?? 'sesiones'
  }

  /**
   * Las cuatro cifras. Si fallan, la franja avisa y las tablas se quedan: perder el resumen es
   * molesto, no poder trabajar sería peor.
   */
  private loadSummary(): void {
    this.loadingSummary = true
    this.api.getSummary().subscribe((r) => {
      this.loadingSummary = false
      this.summary = r.error ? null : r.data
    })
  }
}
